import React, { useRef, useState } from "react";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { ReadableNavButton } from "../Sticker";
import FormLabel from "../FormLabel";
import {
  WorkflowModalShell,
  WorkflowModalHeader,
  WorkflowModalBody,
  WorkflowModalFooter,
} from "../workflow/WorkflowModal";
import {
  buildEventExportUrl,
  buildOrganizerExportUrl,
  downloadGuestListCsv,
  EXPORT_RANGE_OPTIONS,
} from "../../lib/guestListExport";
import { downloadDemoGuestListCsv } from "../../lib/demoHostExperience";

export default function GuestListExportModal({
  onClose,
  eventId,
  eventName,
  tokenParam,
  canExportAllEvents,
  demoMode = false,
  demoRsvps = [],
}) {
  const dialogRef = useRef(null);
  const [scope, setScope] = useState("event");
  const [range, setRange] = useState("last_event");
  const [exporting, setExporting] = useState(false);

  async function handleExport() {
    setExporting(true);
    try {
      if (demoMode) {
        const rowCount = downloadDemoGuestListCsv(demoRsvps, eventName);
        toast.success(rowCount === 0 ? "Exported empty guest list (headers only)" : `Exported ${rowCount} guest${rowCount === 1 ? "" : "s"}`);
        onClose();
        return;
      }
      const isAllEvents = scope === "all";
      const url = isAllEvents ? buildOrganizerExportUrl() : buildEventExportUrl(eventId);
      const params = {
        range,
        token: !isAllEvents && tokenParam ? tokenParam : undefined,
      };
      const fallbackFilename = isAllEvents
        ? "all-events-guest-list.csv"
        : `${(eventName || "event").replace(/[^a-zA-Z0-9-_]+/g, "-")}-guest-list.csv`;

      const { rowCount } = await downloadGuestListCsv({ url, params, fallbackFilename });

      if (rowCount === 0) {
        toast.success("Exported empty guest list (headers only)");
      } else if (rowCount != null) {
        toast.success(`Exported ${rowCount} guest${rowCount === 1 ? "" : "s"}`);
      } else {
        toast.success("Guest list exported");
      }
      onClose();
    } catch (err) {
      toast.error(err.message || "Couldn't export guest list");
    } finally {
      setExporting(false);
    }
  }

  return (
    <WorkflowModalShell
      dialogRef={dialogRef}
      onClose={onClose}
      testId="guest-list-export-modal"
      titleId="guest-list-export-title"
      busy={exporting}
    >
      <WorkflowModalHeader
        title="Export guest list"
        titleId="guest-list-export-title"
        onClose={onClose}
        closeTestId="guest-list-export-close"
      />
      <WorkflowModalBody className="space-y-5">
        <fieldset>
          <legend className="font-ui font-semibold text-[15px] text-ink mb-2">Scope</legend>
          <div className="space-y-2">
            <label className="flex items-start gap-3 cursor-pointer min-h-[44px]">
              <input
                type="radio"
                name="export-scope"
                value="event"
                checked={scope === "event"}
                onChange={() => setScope("event")}
                className="mt-1"
                data-testid="export-scope-event"
              />
              <span>
                <span className="font-ui font-medium text-[15px] text-ink block">This event</span>
                <span className="font-ui text-[13px] text-ink/70">
                  Guests for {eventName || "this event"} across the selected time range.
                </span>
              </span>
            </label>
            <label
              className={`flex items-start gap-3 min-h-[44px] ${canExportAllEvents ? "cursor-pointer" : "opacity-60"}`}
            >
              <input
                type="radio"
                name="export-scope"
                value="all"
                checked={scope === "all"}
                onChange={() => canExportAllEvents && setScope("all")}
                disabled={!canExportAllEvents}
                className="mt-1"
                data-testid="export-scope-all"
              />
              <span>
                <span className="font-ui font-medium text-[15px] text-ink block">All my events</span>
                <span className="font-ui text-[13px] text-ink/70">
                  {canExportAllEvents
                    ? "Unique guests across every event you host."
                    : "Sign in to your host account to export across all events."}
                </span>
              </span>
            </label>
          </div>
        </fieldset>

        <div>
          <FormLabel htmlFor="export-range">Time range</FormLabel>
          <select
            id="export-range"
            className="field w-full mt-1"
            value={range}
            onChange={(e) => setRange(e.target.value)}
            data-testid="export-range-select"
          >
            {EXPORT_RANGE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <p className="mt-2 font-ui text-[13px] text-ink/70">
            Exports are limited to the last 12 months.
          </p>
        </div>
      </WorkflowModalBody>
      <WorkflowModalFooter sticky safeArea>
        <ReadableNavButton
          type="button"
          onClick={handleExport}
          disabled={exporting}
          className="w-full inline-flex items-center justify-center gap-2"
          data-testid="guest-list-export-submit"
        >
          <Download className="w-4 h-4 shrink-0" strokeWidth={2.5} aria-hidden />
          {exporting ? "Exporting…" : "Download CSV"}
        </ReadableNavButton>
      </WorkflowModalFooter>
    </WorkflowModalShell>
  );
}
